/**
 * Resultado normalizado de uma resposta paginada do backend Guedder.
 *
 * Envelope real (verificado em guedder_app/lib/core/data/rest_object.dart +
 * pagination.dart): `{ data: T[], page: { number, totalPages, totalElements, size } }`.
 * Também aceitamos `content` (Spring puro) e array puro por robustez.
 */
export interface PagedResult<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

function asNumber(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function parsePagedResponse<T = unknown>(resp: unknown): PagedResult<T> {
  const r = (resp ?? {}) as Record<string, unknown>;

  const content: T[] = Array.isArray(resp)
    ? (resp as T[])
    : Array.isArray(r['data'])
      ? (r['data'] as T[])
      : Array.isArray(r['content'])
        ? (r['content'] as T[])
        : [];

  const page = (r['page'] ?? {}) as Record<string, unknown>;

  // Aceita `page.*` (envelope Guedder) ou campos flat (`totalElements` etc.).
  const totalElements = asNumber(page['totalElements'] ?? r['totalElements'], content.length);
  const totalPages = asNumber(page['totalPages'] ?? r['totalPages'], content.length ? 1 : 0);
  const pageNumber = asNumber(page['number'] ?? r['pageNumber'] ?? r['number'], 0);
  const pageSize = asNumber(page['size'] ?? r['pageSize'] ?? r['size'], content.length);

  return { content, totalElements, totalPages, pageNumber, pageSize };
}

/** Desembrulha o envelope `{ data }` de respostas single-object (findOne/save). */
export function unwrapData<T = unknown>(resp: unknown): T {
  const r = (resp ?? {}) as Record<string, unknown>;
  return (r['data'] !== undefined ? r['data'] : resp) as T;
}
